/* VakantieVooruitblik — banner die per week het aantal kind-vakantie meldingen
   toont plus een historie-gebaseerde voorspelling. Klik op een week springt naar
   die week in de rooster. Extracted uit RoosterPage.jsx (feb 2026 split). */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";

export function VakantieVooruitblik({ weeks = 12, onWeekClick, locationId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancel = false;
    const params = { weeks };
    if (locationId) params.location_id = locationId;
    api.get("/planning/vakantie-vooruitblik", { params })
      .then(r => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData({ weeks: [], totaal_meldingen: 0 }); });
    return () => { cancel = true; };
    // `api` is een stabiele module-level import — hoort niet in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks, locationId]);

  if (!data) return null;
  const totaal = data.totaal_meldingen || 0;

  return (
    <div className="mb-3 rounded-md border border-neutral-200 bg-gradient-to-r from-amber-50/50 to-white overflow-hidden"
         data-testid="vakantie-vooruitblik">
      <div className="px-3 py-1.5 flex items-center justify-between gap-2 border-b border-neutral-100 flex-wrap">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-700">
          Vakantie-vooruitblik · komende {weeks} weken
        </div>
        <div className="text-[11px] text-neutral-600 flex items-center gap-3">
          <span>
            {totaal === 0
              ? "geen vakantie-meldingen"
              : <><span className="font-bold tabular-nums">{totaal}</span> kind-vakantieweek(en) gemeld</>}
          </span>
          {data.historie_beschikbaar && data.totaal_voorspeld > 0 && (
            <span className="text-blue-700" data-testid="vakantie-voorspelling-badge">
              voorspelling: <span className="font-bold tabular-nums">~{data.totaal_voorspeld}</span> extra o.b.v. historie
            </span>
          )}
          {!data.historie_beschikbaar && (
            <span className="text-neutral-400 text-[10px]">nog geen historische data voor voorspelling</span>
          )}
        </div>
      </div>
      <div className="flex overflow-x-auto">
        {data.weeks.map(w => {
          const heat = w.kinderen_vakantie;
          const voorspeld = w.voorspeld_kinderen_vakantie || 0;
          const eff = Math.max(heat, voorspeld);
          const bg = eff === 0 ? "bg-neutral-50 text-neutral-400"
                    : eff <= 2 ? "bg-amber-50 text-amber-900"
                    : eff <= 5 ? "bg-amber-100 text-amber-900"
                    : "bg-red-100 text-red-900 font-semibold";
          const isPrediction = heat === 0 && voorspeld > 0;
          return (
            <button key={w.week_start}
                    onClick={() => onWeekClick && onWeekClick(w.week_start)}
                    className={`flex-1 min-w-[54px] px-2 py-1.5 border-r border-neutral-100 last:border-r-0 text-center hover:brightness-95 transition ${bg}`}
                    data-testid={`vakantie-week-${w.iso_week}`}
                    title={`Week ${w.iso_week} (${w.week_start} → ${w.week_end})\n` +
                        `Gemeld: ${heat}` +
                        (voorspeld > 0 ? `\nVoorspeld o.b.v. historie: ${voorspeld} (${w.voorspelling_bron})` : "")}
            >
              <div className="text-[10px] leading-none opacity-75">wk {w.iso_week}</div>
              <div className="text-sm font-bold tabular-nums leading-none mt-1">
                {heat || "—"}
              </div>
              {isPrediction && (
                <div className="text-[9px] text-blue-700 italic leading-none mt-0.5" data-testid={`vakantie-voorspeld-${w.iso_week}`}>
                  ~{voorspeld}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
